import React from 'react'
import { Search, Calendar } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle, Input, Select, SelectOption } from '../ui'
import type { AttendanceEventFilters, EventType } from '../../types/attendance.types'
import type { Employee } from '../../hooks/useEmployees'

interface Location {
  id: string
  name: string
  address?: string | null
}

interface PaginatedEmployees {
  data: Employee[]
  total: number
  page: number
  limit: number
  totalPages: number
}

interface LocationsData {
  data: Location[]
  total: number
  page: number
  limit: number
}

interface AttendanceFiltersProps {
  filters: AttendanceEventFilters
  onFiltersChange: (filters: Partial<AttendanceEventFilters>) => void
  employees?: PaginatedEmployees
  locations?: LocationsData
}

export function AttendanceFilters({
  filters,
  onFiltersChange,
  employees,
  locations,
}: AttendanceFiltersProps) {
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({ search: e.target.value || undefined, page: 1 })
  }

  const handleEmployeeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onFiltersChange({ employeeId: e.target.value || undefined, page: 1 })
  }

  const handleLocationChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onFiltersChange({ locationId: e.target.value || undefined, page: 1 })
  }

  const handleEventTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onFiltersChange({ eventType: (e.target.value as EventType) || undefined, page: 1 })
  }

  const handleDateFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({ dateFrom: e.target.value || undefined, page: 1 })
  }

  const handleDateToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({ dateTo: e.target.value || undefined, page: 1 })
  }

  const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onFiltersChange({ sort: e.target.value as 'ASC' | 'DESC' })
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Фильтры и поиск</CardTitle>
        <CardDescription>
          Используйте фильтры для поиска событий посещаемости
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Поиск по сотруднику..."
              value={filters.search || ''}
              onChange={handleSearchChange}
              className="pl-10"
            />
          </div>

          {/* Employee filter */}
          <Select
            placeholder="Сотрудник"
            value={filters.employeeId || ''}
            onChange={handleEmployeeChange}
          >
            <SelectOption value="">Все сотрудники</SelectOption>
            {employees?.data?.map((emp) => (
              <SelectOption key={emp.id} value={emp.id}>
                {emp.last_name} {emp.first_name}
              </SelectOption>
            ))}
          </Select>

          {/* Location filter */}
          <Select
            placeholder="Локация"
            value={filters.locationId || ''}
            onChange={handleLocationChange}
          >
            <SelectOption value="">Все локации</SelectOption>
            {locations?.data?.map((loc) => (
              <SelectOption key={loc.id} value={loc.id}>
                {loc.name}
              </SelectOption>
            ))}
          </Select>

          {/* Event type filter */}
          <Select
            placeholder="Тип события"
            value={filters.eventType || ''}
            onChange={handleEventTypeChange}
          >
            <SelectOption value="">Все типы</SelectOption>
            <SelectOption value="check_in">Приход</SelectOption>
            <SelectOption value="check_out">Уход</SelectOption>
            <SelectOption value="break_start">Начало перерыва</SelectOption>
            <SelectOption value="break_end">Конец перерыва</SelectOption>
            <SelectOption value="auto_close">Автозакрытие</SelectOption>
          </Select>

          {/* Date from */}
          <div className="relative">
            <Calendar className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              type="date"
              placeholder="Дата от"
              value={filters.dateFrom || ''}
              onChange={handleDateFromChange}
              className="pl-10"
            />
          </div>

          {/* Date to */}
          <div className="relative">
            <Calendar className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              type="date"
              placeholder="Дата до"
              value={filters.dateTo || ''}
              onChange={handleDateToChange}
              className="pl-10"
            />
          </div>

          {/* Sort */}
          <Select value={filters.sort || 'DESC'} onChange={handleSortChange}>
            <SelectOption value="DESC">Сначала новые</SelectOption>
            <SelectOption value="ASC">Сначала старые</SelectOption>
          </Select>
        </div>
      </CardContent>
    </Card>
  )
}